// ============================================================================
// CASE STATE DATA SERVICE
// ============================================================================

import { get, post, del } from '../client/axiosClient';
import { caseStateDataEndpoints, CASE_STATE_DATA_BASE_PATH } from '../endpoints/caseStateData.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, EmptyResponse, ID } from '../types';
import type {
  CaseStateData,
  CaseStateDataQueryParameters,
  SaveCaseStateDataRequest,
  SaveCaseStateDataResponse,
} from '../types/caseStateData.types';

class CaseStateDataService {
  /** List entries for a case. Returns either latest-per-state or all entries based on the request. */
  async list(params: CaseStateDataQueryParameters): Promise<ApiResponse<CaseStateData[]>> {
    const endpoint = buildEndpointWithQuery(CASE_STATE_DATA_BASE_PATH, params as Record<string, any>);
    const response = await get<ApiResponse<CaseStateData[]>>(endpoint);
    return response.data;
  }

  async getById(id: ID): Promise<ApiResponse<CaseStateData>> {
    const response = await get<ApiResponse<CaseStateData>>(caseStateDataEndpoints.getById(id));
    return response.data;
  }

  /**
   * Save form data for a (case, state). The server:
   *   1. Resolves workflow_state by stateCode (within the case's workflow definition).
   *   2. Reads the state's form_spec and walks denorm_to mappings.
   *   3. Computes snapshot columns from the data values.
   *   4. INSERTs a new entry (when allow_multi_entry=true OR no existing row),
   *      or UPDATEs the existing entry in-place otherwise.
   */
  async save(request: SaveCaseStateDataRequest): Promise<SaveCaseStateDataResponse> {
    const response = await post<SaveCaseStateDataResponse>(caseStateDataEndpoints.save(), request);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(caseStateDataEndpoints.delete(id));
    return response.data;
  }
}

export const caseStateDataService = new CaseStateDataService();
export default caseStateDataService;
